import {observable, computed} from 'mobx';

import {PartialGameDomainModel} from "./partial-game-domain-model";
import {CompetitionViewModel} from "../../common/competition/models/competition-view-model";
import {GameCompetitorViewModel} from "./game-competitor/game-competitor-view-model";
import {SportViewModel} from "../../sports/models/sport-view-model";
import {contractsUtils} from "../../../infrastructure/utils/contracts-utils";
import {GameCenterLinkModel} from "../../../containers/game-center-container/models/game-center.link-model";
import {GAME_STATUSES} from "../../../core/app/consts";
import {EntityViewModel} from "../../../infrastructure/models/entity/entity.view-model";
import {GameH2hExtraDataApiModel} from "../../h2h/models/game/game-h2h.extra-data.api-model";
import {GameCenterHashParamsModel} from "../../../containers/game-center-container/models/game-center.hash-params-model";

export class PartialGameViewModel extends EntityViewModel {
  domainModel: PartialGameDomainModel;
  @observable winnerStatus: Number;
  @observable date: String;
  @observable time: String;

  constructor(partialGameDomainModel: PartialGameDomainModel, date: String, time: String, winnerStatus: Number) {
    super(partialGameDomainModel);

    contractsUtils.required(partialGameDomainModel);

    this.winnerStatus = winnerStatus;
    this.updateTime = partialGameDomainModel.updateTime;
    this.date = date;
    this.time = time;
    this.athleteNotInSquad = partialGameDomainModel.athleteNotInSquad;
  }

  @computed get showCountdown(): Boolean {
    return this.domainModel.showCountdown
  }

  @computed get competitionDisplayName(): String {
    return this.domainModel.competitionDisplayName
  }

  @computed get matchupId(): Boolean {
    return this.domainModel.matchupId;
  }

  @computed get sport(): SportViewModel{
    return new SportViewModel(this.domainModel.sport);
  }

  @computed get competition(): CompetitionViewModel{
    return new CompetitionViewModel(this.domainModel.competition);
  }

  @computed get awayCompetitor(): GameCompetitorViewModel{
    return  new GameCompetitorViewModel(this.domainModel.awayCompetitor);
  }

  @computed get homeCompetitor(): GameCompetitorViewModel{
    return new GameCompetitorViewModel(this.domainModel.homeCompetitor);
  }

  get id(): Number {
    return this.domainModel.id;
  }

  get isFollow(): Boolean {
    return this.domainModel.isFollow;
  }

  get linkModel(): GameCenterLinkModel {
    const hashParams: GameCenterHashParamsModel = new GameCenterHashParamsModel({id: this.id});
    const linkModel: GameCenterLinkModel = new GameCenterLinkModel(this.matchupId, this.competition.sport.nameForURL, this.competition.nameForURL, this.nameForUrl, hashParams, this.isFollow);

    return linkModel;
  }

  get nameForUrl(): String {
    return this.domainModel.nameForUrl;
  }

  @computed get statusGroup(): GAME_STATUSES {
    return this.domainModel.statusGroup;
  }

  @computed get statusText(): String {
    return this.domainModel.statusGroup === GAME_STATUSES.scheduled ? '' :  this.domainModel.statusText;
  }

  @computed get hasLineups(): Boolean {
    return this.domainModel.hasLineups;
  }

  @computed get hasMissingPlayers(): Boolean {
    return this.domainModel.hasMissingPlayers;
  }

  @computed get hasFieldPositions(): Boolean {
    return this.domainModel.hasFieldPositions;
  }

  @computed get hasTVNetworks(): Boolean {
    return this.domainModel.hasTVNetworks;
  }

  @computed get hasBetsTeaser(): Boolean {
    return this.domainModel.hasBetsTeaser;
  }

  @computed get winDescription():String {
    return this.domainModel.winDescription;
  }

  @computed get roundNum(): Number {
    return this.domainModel.roundNum;
  }

  @computed get aggregateText():String {
    return this.domainModel.aggregateText;
  }

  @computed get gameTimeDisplay(): String {
    return this.domainModel.gameTimeDisplay;
  }

  get gameTimeAndStatusDisplayType(): Number {
    return this.domainModel.gameTimeAndStatusDisplayType;
  }

  get displayTitle(): String {
    return this.domainModel.displayTitle;
  }

  @computed get gameTimeAndStatusText() {
    return this.domainModel.gameTimeAndStatusText;
  }

  @computed get startTime(): Date {
    return this.domainModel.startTime;
  }

  @computed get isLive(): Boolean {
    return this.domainModel.statusGroup === GAME_STATUSES.started;
  }

  @computed get isScheduled(): Boolean {
    return this.domainModel.statusGroup === GAME_STATUSES.scheduled;
  }

  @computed get isEnded(): Boolean {
    return this.domainModel.statusGroup === GAME_STATUSES.ended;
  }

  @computed get justEnded(): Boolean {
    return this.domainModel.justEnded;
  }

  @computed get outcome(): Boolean {
    return this.domainModel.outcome;
  }

  @computed get extraDataTitle(): String {
    return this.domainModel.extraDataTitle;
  }

  @computed get extraData(): Array<GameH2hExtraDataApiModel> {
    return this.domainModel.extraData;
  }

  @computed get lineupsStatus(): Number {
    return this.domainModel.lineupsStatus;
  }

  @computed get lineupsStatusText(): Number {
    return this.domainModel.lineupsStatusText;
  }

  compareByDateAndRound(model: PartialGameViewModel) {
    if (this.domainModel.startTime > model.domainModel.startTime) {
      return 1;
    }

    if (this.domainModel.startTime < model.domainModel.startTime) {
      return -1
    }

    if (this.roundNum > model.roundNum) {
      return 1;
    }

    if (this.roundNum < model.roundNum) {
      return -1;
    }

    return 0;
  }

  compare(model: PartialGameViewModel) {
    return this.compareByDateAndRound(model);
  }

  toString(): String {
    const homeCompetitorName: String = this.homeCompetitor.name;
    const awayCompetitorName: String = this.awayCompetitor.name;

    const homeCompetitorScore: String = this.homeCompetitor.score;
    const awayCompetitorScore: String = this.awayCompetitor.score;

    return `${homeCompetitorName} ${homeCompetitorScore} - ${awayCompetitorScore} ${awayCompetitorName}\n${this.date}`
  }

  get popularityRank() {
    return this.homeCompetitor.popularityRank + this.awayCompetitor.popularityRank;
  }

  get outcomeStatusEndOfGame(): Boolean {
    return this.domainModel.outcomeStatusEndOfGame;
  }

  get tvNetworkName(): String {
    return this.domainModel.tvNetworkName;
  }
}
